async function sha256KeyFromPassword(password: string): Promise<CryptoKey> {
    const encoder = new TextEncoder()
    const hash = await crypto.subtle.digest('SHA-256', encoder.encode(password))
    return crypto.subtle.importKey('raw', hash, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt'])
}

export async function encrypt(text: string, password: string): Promise<string> {
    const iv = crypto.getRandomValues(new Uint8Array(12)) // 96-bit IV for AES-GCM
    const key = await sha256KeyFromPassword(password)
    const encodedText = new TextEncoder().encode(text)
    const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encodedText)

    // Encode IV + ciphertext together (base64 for easy transport)
    const combined = new Uint8Array(iv.length + ciphertext.byteLength)
    combined.set(iv)
    combined.set(new Uint8Array(ciphertext), iv.length)

    return btoa(String.fromCharCode(...combined))
}

export async function decrypt(encryptedBase64: string, password: string): Promise<string> {
    const combined = Uint8Array.from(atob(encryptedBase64), (c) => c.charCodeAt(0))
    const iv = combined.slice(0, 12)
    const ciphertext = combined.slice(12)
    const key = await sha256KeyFromPassword(password)

    const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext)

    return new TextDecoder().decode(decrypted)
}

export async function sha256(input: string): Promise<string> {
    const encoder = new TextEncoder()
    const data = encoder.encode(input)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    return [...new Uint8Array(hashBuffer)].map((b) => b.toString(16).padStart(2, '0')).join('')
}
